import { PrioritySemaphore, makePriority } from '@mimir/common/async/prioritySemaphore';
import type { AdditionalParams, ApiClient, HttpMethod } from '@mimir/common/util/apiClient';
import type { AuthTokenValue } from '@mimir/common/client/auth';
import { httpClientMimirJwtAuthHeaderDict } from '@mimir/common/client/auth';
import { invariant } from '@mimir/common/util/invariant';
import { httpErrorStatus } from '@mimir/common/util/httpError';
import { abortableSleep } from '@mimir/common/async/abortableSleep';
import type { Axios } from 'axios';
import axios from 'axios';

const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

/*
  Since we specify a timeout for HTTP requests, we must also limit the number of
  concurrent in-flight requests to something sensible to give the server a fair
  chance at responding to all of the requests within the timeout duration.

  Here we limit the number of in-flight requests to 10.
*/
const semaphore = new PrioritySemaphore(10);

/*
  TODO: Remove use of global state.
*/
export async function callApi(
  url: string,
  method: HttpMethod,
  additionalParams: AdditionalParams = {},
  body?: any,
) {
  const jwtFromGlobalState = await getJwtToken();
  const token: AuthTokenValue = {
    type: 'NativeJwtToken',
    jwtSecret: jwtFromGlobalState,
  };

  const headers = {
    ...additionalParams.headers,
    ...httpClientMimirJwtAuthHeaderDict(token),
  };
  // This preserves a behaviour of the previously used aws-api-gateway-client,
  // that would set Content-Type and Accept headers if not present
  if (headers['Content-Type'] === undefined && body) {
    headers['Content-Type'] = 'application/json';
  }
  if (headers['Accept'] === undefined) {
    headers['Accept'] = 'application/json';
  }

  additionalParams.headers = headers;
  const req: RequestInfo = {
    method,
    url,
    queryParams: additionalParams.queryParams,
    data: body || undefined, // Axios does not like null bodies
  };


  return callWithAxios(axios, req, additionalParams);
}


interface RequestInfo {
  method: HttpMethod;
  url: string;
  queryParams?: Record<string, unknown>;
  data?: string;
}

async function callWithAxios(axiosClient: Axios, reqInfo: RequestInfo, additionalParams: AdditionalParams) {
  const priority = additionalParams.priority ?? makePriority();
  const cancelToken = additionalParams.cancelToken ?? null;
  let tries = 0;
  let retryBlocker: string | undefined;
  while (true) {
    try {
      const result = await semaphore.limit(async () => {
        const response = await axiosClient.request({
          method: reqInfo.method,
          url: reqInfo.url,
          params: reqInfo.queryParams,
          timeout: additionalParams.timeout ?? DEFAULT_REQUEST_TIMEOUT_MS,
          data: reqInfo.data,
          headers: additionalParams.headers,
          signal: additionalParams.signal,
          paramsSerializer: serializeQueryParams,
        });
        return response;
      }, priority, cancelToken);
      if (tries > 0) {
        console.log('Call ' + reqInfo.url + ' succeeded after ' + tries + ' tries.');
      }
      return result;
    } catch (err) {
      ++tries;
      if (additionalParams.signal != null) {
        const retryInfo = getRetryInfo(reqInfo.method, err);
        if (retryInfo.canRetry) {
          // eslint-disable-next-line no-bitwise
          const delay = 100 * (1 << tries);
          console.warn('Will retry API call ' + reqInfo.url + ' in ' + delay + ' msec, due to ' + retryInfo.reason);
          try {
            await abortableSleep(delay, additionalParams.signal);
            continue;
          } catch(_) {
            /*
              Ignore sleep cancellation in favor of reporting the original
              error below.
            */
          }
        } else {
          retryBlocker = retryInfo.reason;
        }
      } else {
        retryBlocker = 'no abort signal to bound try attempts';
      }

      const info = {
        tries,
        retryBlocker,
        ...reqInfo
      };
      const errWithInfo = err as { info?: Record<string, unknown> };
      if (errWithInfo.info) {
        Object.assign(info, errWithInfo.info);
      }

      errWithInfo.info = info;
      throw errWithInfo;
    }
  }
}

interface RetryInfo {
  canRetry: boolean;
  reason: string;
}

function getRetryInfo(method: string, error: unknown): RetryInfo {
  const code = httpErrorStatus(error);
  if (code === 429) {
    return { canRetry: true, reason: 'rate limited request' };
  }

  if (code === 503) {
    if ((method === 'GET' || method === 'PUT')) {
      return { canRetry: true, reason: 'service unavailable for idempotent method' };
    }
    return { canRetry: false, reason: 'method not idempotent' };
  }

  return { canRetry: false, reason: 'HTTP code not considered retryable' };
}

export function dynamicApiClient(
): ApiClient {
  return {
    call: callApi,
    refreshAuth: () => getGlobalJwtTokenProvider().refresh()
  };
}

export interface JwtTokenProvider {
  getJwtToken(): Promise<string>;
  refresh(): Promise<void>;
}

let globalJwtTokenProvider: JwtTokenProvider | null = null;

export function installGlobalJwtTokenProvider(provider: JwtTokenProvider, silent = false) {
  if (globalJwtTokenProvider != null && !silent) {
    /*
      TODO: This kind of trampling on global state is error prone and should be
      avoided.
    */
    console.warn('Overriding previously installed token provider.');
  }
  globalJwtTokenProvider = provider;
}

function getGlobalJwtTokenProvider() {
  invariant(globalJwtTokenProvider != null, 'Expected installGlobalJwtTokenProvider() to already have been called.');
  return globalJwtTokenProvider;
}

export const getJwtToken = async () => {
  return getGlobalJwtTokenProvider().getJwtToken();
};

/**
 * Constructs a query string where arrays are formatted
 * as comma separated values, so that
 * { key: ["value1", "value2"] } -> key=value1,value2.
 *
 * This implementation follows that of the aws-api-gateway-client lib
 * (see aws-api-gateway-client/simpleHttpClient/buildCanonicalQueryString)
 * we previously used, and we have backend code expecting this
 * format; parsing event.queryStringParameters instead of using
 * the event.multiValueQueryStringParameters provided by API Gateway).
 */
function serializeQueryParams(
  queryParams: Record<string, unknown>
): string {
  if (Object.keys(queryParams || {}).length === 0) {
    return '';
  }

  let qs = '';
  for (const [key, val] of Object.entries(queryParams)) {
    const encodedKey = encodeURIComponent(key);
    const encodedVal = encodeURIComponent(String(val));
    qs += encodedKey + '=' + encodedVal + '&';
  }
  return qs.substring(0, qs.length - 1);
}
